// =============================================
// frontend/src/pages/LoginPage.js
// =============================================
// Ye page tab dikhata hai jab user authenticated nahi hai.
// URL mein ?token= hona chahiye (invitation link se aaya ho).
// "Sign in with Microsoft" button user ko backend pe bhejta hai.
// Backend wahan se Microsoft ke login page pe redirect karta hai.
import React from 'react';
import { AlertCircle, PartyPopper, LockKeyhole } from 'lucide-react';

const LoginPage = ({ token, error }) => {
  // Error messages ko human-readable banate hain
  const errorMessages = {
    no_token: 'No invitation link found. Please use a valid invite link.',
    invalid_link: 'This invitation link is invalid or has been revoked.',
    expired: 'This invitation link has expired. Please request a new one.',
    max_uses: 'This invitation link has reached its maximum use limit.',
    invalid_token: 'Invalid invitation link. Please request a new one.',
    auth_failed: 'Authentication failed. Please try again.',
  };

  const errorMessage = error
    ? errorMessages[error] || decodeURIComponent(error)
    : null;

  // Microsoft login page pe redirect karte hain
  // Token bhi saath mein bhejte hain taaki session mein save ho sake
  const handleMicrosoftSignIn = () => {
    if (!token) return;
    // Direct browser URL change Frontend (3000) se nikal kar Backend (5000) pe jani chahiye
    // CRA proxy window.location.href ko proxy nahi karta
    const backendUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    window.location.href = `${backendUrl}/auth/microsoft?token=${encodeURIComponent(token)}`;
  };

  return (
    <div className="login-page">
      <div className="login-card">
        {/* Brand Header */}
        <div className="login-brand">
          <div className="login-brand-icon">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <rect width="36" height="36" rx="8" fill="url(#grad)" />
              <path d="M10 26 L18 10 L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M13 21 L23 21" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="36" y2="36">
                  <stop offset="0%" stopColor="#2563EB" />
                  <stop offset="100%" stopColor="#06B6D4" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div>
            <h1 className="login-title">Screenshot Studio</h1>
            <p className="login-subtitle">App-store creative workspace</p>
          </div>
        </div>

        {/* Content */}
        <div className="login-content">
          {errorMessage ? (
            /* Error State — Invalid or expired link */
            <>
              <div className="login-error-icon">
                <AlertCircle size={48} color="#ef4444" strokeWidth={1.5} />
              </div>
              <h2 className="login-heading">Access Denied</h2>
              <p className="login-description">{errorMessage}</p>
              <p className="login-hint">
                Please contact your administrator for a new invitation link.
              </p>
            </>
          ) : token ? (
            /* Valid token — Show sign in button */
            <>
              <div className="login-welcome-icon">
                <PartyPopper size={48} color="#4ade80" strokeWidth={1.5} />
              </div>
              <h2 className="login-heading">Welcome to Screenshot Studio</h2>
              <p className="login-description">
                You've been invited to access Screenshot Studio. Sign in with your Microsoft
                account to continue.
              </p>
              <button
                type="button"
                className="microsoft-signin-btn"
                onClick={handleMicrosoftSignIn}
              >
                <svg className="ms-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="20" height="20">
                  <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                  <path fill="#f35325" d="M1 1h10v10H1z" />
                  <path fill="#81bc06" d="M12 1h10v10H12z" />
                  <path fill="#05a6f0" d="M1 12h10v10H1z" />
                  <path fill="#ffba08" d="M12 12h10v10H12z" />
                </svg>
                Sign in with Microsoft
              </button>
              <p className="login-hint">
                Only the invited Microsoft account can access this tool.
              </p>
            </>
          ) : (
            /* No token in URL — no invite link */
            <>
              <div className="login-error-icon">
                <LockKeyhole size={48} color="#f59e0b" strokeWidth={1.5} />
              </div>
              <h2 className="login-heading">Access Restricted</h2>
              <p className="login-description">
                This tool requires a valid invitation link to access.
              </p>
              <p className="login-hint">
                Please request an invitation from your administrator.
              </p>
            </>
          )}
        </div>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%);
          padding: 2rem;
          font-family: 'Inter', system-ui, sans-serif;
        }

        .login-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 3rem 2.5rem;
          max-width: 440px;
          width: 100%;
          backdrop-filter: blur(20px);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
          text-align: center;
          color: #fff;
        }

        .login-brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 2.5rem;
          padding-bottom: 2rem;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .login-brand-icon {
          flex-shrink: 0;
        }

        .login-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0;
          color: #fff;
          text-align: left;
        }

        .login-subtitle {
          font-size: 0.78rem;
          color: rgba(255,255,255,0.5);
          margin: 0;
          text-align: left;
        }

        .login-welcome-icon,
        .login-error-icon {
          font-size: 3rem;
          margin-bottom: 1.25rem;
        }

        .login-heading {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 1rem;
          color: #fff;
        }

        .login-description {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.7);
          line-height: 1.6;
          margin: 0 0 1.5rem;
        }

        .login-hint {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.4);
          margin-top: 1.25rem;
        }

        .microsoft-signin-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          background: #fff;
          color: #1a1a2e;
          border: none;
          border-radius: 8px;
          padding: 0.85rem 1.75rem;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
          width: 100%;
          justify-content: center;
          letter-spacing: 0.01em;
        }

        .microsoft-signin-btn:hover {
          background: #f0f4ff;
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.3);
        }

        .microsoft-signin-btn:active {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
